import * as React from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDownIcon } from "lucide-react";

import { cn } from "@bcc/lib/utils";
import { setAccordionItemTestId, setAccordionTriggerTestId, setAccordionContentTestId } from "./accordion.utils";

interface AccordionItemProps extends React.ComponentProps<typeof AccordionPrimitive.Item> {
  /** When true, the last item in the accordion keeps its bottom border instead of the default "last:border-b-0" removal. */
  showLastBorder?: boolean;
  testId?: string;
}

function Accordion({ ...props }: React.ComponentProps<typeof AccordionPrimitive.Root>) {
  return <AccordionPrimitive.Root data-slot="accordion" {...props} />;
}

function AccordionItem({ className, testId, value, showLastBorder, ...props }: Readonly<AccordionItemProps>) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      data-testid={testId ?? setAccordionItemTestId(value)}
      value={value}
      className={cn("border-b", !showLastBorder && "last:border-b-0", className)}
      {...props}
    />
  );
}

function AccordionTrigger({
  className,
  children,
  testId,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger> & {
  testId?: string;
}) {
  const triggerLabel = typeof children === "string" ? children : "trigger";
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        data-testid={testId ?? setAccordionTriggerTestId(triggerLabel)}
        className={cn(
          "focus-visible:border-ring focus-visible:ring-ring/50 flex flex-1 items-start justify-between gap-4 rounded-md py-4 text-left text-sm font-medium transition-all outline-none hover:underline focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]>svg]:rotate-180",
          className
        )}
        {...props}
      >
        {children}
        <ChevronDownIcon className="text-muted-foreground pointer-events-none size-4 shrink-0 translate-y-0.5 transition-transform duration-200" />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

function AccordionContent({
  className,
  children,
  testId,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content> & {
  testId?: string;
}) {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      data-testid={testId ?? setAccordionContentTestId("content")}
      className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down data-[state=closed]:h-0 overflow-hidden text-sm"
      {...props}
    >
      <div className={cn("pt-0 pb-4", className)}>{children}</div>
    </AccordionPrimitive.Content>
  );
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
